import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FunnelIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';
import { Dropdown } from 'components/Form/Dropdown';

type DropdownValue = {
  label: string;
  value: string;
};

type DropdownFilterProps = {
  label?: string;
  filterKey: string;
  dropdownValues: Array<DropdownValue>;
  showAllLabel?: string;
  defaultToFirstValue?: boolean;
  className?: string;
  testId?: string;
  currentValue: string;
  onChange: (filterKey: string, newValue: string) => void;
  hideValueFromButton?: boolean;
  multiple?: boolean;
  isLoading?: boolean;
};

const LOADING_OPTION_VALUE = '__procuros_loading';

export const DropdownFilter: FC<DropdownFilterProps> = ({
  label,
  filterKey,
  currentValue,
  dropdownValues,
  showAllLabel,
  defaultToFirstValue,
  onChange,
  className,
  testId,
  hideValueFromButton,
  multiple,
  isLoading,
}) => {
  const { t } = useTranslation();
  const defaultValue = useMemo(
    () => ({
      value: '_procuros_all',
      label: showAllLabel ?? t('common:allValues'),
    }),
    [showAllLabel, t],
  );

  const translatedDropdownValues = useMemo(
    () =>
      dropdownValues.map((dropdownValue: DropdownValue) => ({
        label: dropdownValue.label,
        value: dropdownValue.value,
      })),
    [dropdownValues],
  );

  const options = useMemo(() => {
    if (isLoading) {
      return [{ value: LOADING_OPTION_VALUE, label: 'common:loading', disabled: true }];
    }
    if (defaultToFirstValue) {
      return translatedDropdownValues;
    }
    return [defaultValue, ...translatedDropdownValues];
  }, [isLoading, defaultToFirstValue, defaultValue, translatedDropdownValues]);

  const value = useMemo(() => {
    const baseValue = currentValue || (defaultToFirstValue ? options[0]?.value : defaultValue.value);
    return baseValue;
  }, [currentValue, defaultToFirstValue, options, defaultValue.value]);

  // Create a reusable icon component
  const filterIcon = <DropdownFilterIcon currentValue={currentValue} />;

  // Common properties for both single and multiple mode
  const commonProps = {
    className,
    dropdownDisplayTestId: testId,
    options,
    emptySelectionLabel: label,
    showSelection: !hideValueFromButton,
    leftIcon: filterIcon,
  };
  const handleChange = (newValue: string | Array<string>) => {
    const selectedValue = Array.isArray(newValue) ? newValue : [newValue];
    const isAllSelected = selectedValue.at(-1) === defaultValue.value;
    const filteredValue = selectedValue.filter((v) => v !== defaultValue.value);
    const finalValue = isAllSelected ? '' : Array.isArray(newValue) ? filteredValue.join(',') : newValue;

    onChange(filterKey, finalValue);
  };

  // Cast to generic component with the correct type
  const DropdownComponent = Dropdown as typeof Dropdown<string>;

  //Typescript issues, that's why the check by multiple
  return multiple ? (
    <DropdownComponent
      {...commonProps}
      value={value.split(',')}
      onChange={handleChange}
      multiple={true}
      searchEnabled
    />
  ) : (
    <DropdownComponent {...commonProps} value={value} onChange={handleChange} multiple={multiple} searchEnabled />
  );
};

type DropdownFilterIconProps = {
  currentValue: string;
  className?: string;
};
const DropdownFilterIcon: FC<DropdownFilterIconProps> = ({ currentValue, className }) =>
  currentValue ? (
    <span
      className={cn(
        'flex size-5 items-center justify-center rounded-full border border-procuros-green-500 bg-procuros-green-50 text-procuros-green-500',
        className,
      )}
    >
      {currentValue.split(',').length}
    </span>
  ) : (
    <FunnelIcon className={className} />
  );
